/**
 * @file IncomingCall.jsx
 * @description IncomingCall component for displaying and managing incoming calls.
 * @component IncomingCall
 * @exports IncomingCall - Main component for displaying and managing incoming calls.
 */

import React, { useState, useRef, useEffect } from "react";
import { Button, Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import { CallClient } from "@azure/communication-calling";

import { acceptIvrJob } from "../../../services/ivrModule/jobServices";
import { completeIvrAssignment, closeIvrAssignment } from "../../../services/ivrModule/assignmentServices";
import useJobOfferPolling from "./offerpollingCalls.jsx";
import { initiateCall } from "../../../services/ivrModule/callServices";

// Import constants
import {
  INCOMING_CALLS_STRINGS,
  INCOMING_CALLS_ERRORS,
  SUCCESS_MESSAGES,
  CONSOLE_MESSAGES,
  CALL_STATES,
  CALL_CONFIG,
  STORAGE_KEYS,
  PERMISSION_TYPES,
  PERMISSION_STATES,
  HTTP_STATUS,
  ANIMATION_CONFIG,
  UI_CONFIG,
} from "../../../common/constants/agentModule/incomingCallsConstants";
import { DISPLAY, VARIANTS } from "../../../common/constants/commonConstants";

const IncomingCall = ({onCustomerGuid, onChannelReference}) => {
  /* const ACS_TOKEN = localStorage.getItem("ACSIdentityToken");
  const workerId = localStorage.getItem("workerid");
  const agentAcsId = localStorage.getItem("acsIdentity");
  console.log ("worker id is: ", workerId)

  const { jobOffer, setJobOffer, blink } = useJobOfferPolling(workerId); */

  const [workerId, setWorkerId] = useState(null);
  const [agentAcsId, setAgentAcsId] = useState(null);
  const [ACSIdentityToken, setACSIdentityToken] = useState(null);
  //const [jobOffer, setJobOffer] = useState(null);
  //const [blink] = useState(false)

  const [acceptedOffer, setAcceptedOffer] = useState(null);
  const [callState, setCallState] = useState(null);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [assignmentId, setAssignmentId] = useState(null);

  const callAgentRef = useRef(null);
  const callRef = useRef(null);
  //const [setCallAgent] = useState(null);
  //const [setCall] = useState(null);

  const [callAgent, setCallAgent] = useState(null);
  const [call, setCall] = useState(null)

  useEffect(() => {
    setWorkerId(localStorage.getItem(STORAGE_KEYS.WORKER_ID));
    setAgentAcsId(localStorage.getItem(STORAGE_KEYS.ACS_IDENTITY));
    setACSIdentityToken(localStorage.getItem(STORAGE_KEYS.ACS_IDENTITY_TOKEN));
  }, []);

  const { jobOffer, setJobOffer, blink } = useJobOfferPolling(workerId);

  useEffect(() => {
    onCustomerGuid(jobOffer ? jobOffer.customerguid : null);
    onChannelReference(jobOffer ? jobOffer.channelProfile : null);
  }, [jobOffer, onCustomerGuid, onChannelReference]);

  // Add guard to delay rendering
  if (!workerId || !agentAcsId) return <div>{INCOMING_CALLS_STRINGS.LOADING}</div>;


  //const { hangUp: controlHangUp } = useCallControls(tokenCredential, acceptedOffer?.channelReference);

  const handleAcceptOffer = async () => {
    if (!jobOffer) return;
    try {
      const response = await acceptIvrJob({
        workerId,
        offerId: jobOffer.offerId,
        channelReference: jobOffer.channelReference,
        customerAcsId: jobOffer.customerAcsId,
        agentAcsId,
      });
  
      const assignmentId = response.policy.assignmentId;
      if (assignmentId) {
        setAssignmentId(response.policy.assignmentId); 
      }
  
      setAcceptedOffer(jobOffer);
      setJobOffer(null);
    } catch (err) {
      console.error(INCOMING_CALLS_ERRORS.ACCEPT_OFFER_FAILED, err);
    }
  };
  

  
  const handleInitiateCall = async () => {
    try {
      if (!acceptedOffer?.customerAcsId || !agentAcsId) {
        console.error(INCOMING_CALLS_ERRORS.MISSING_ACS_IDS);
        return;
      }
  
      const tokenCredential = new AzureCommunicationTokenCredential(ACSIdentityToken);
      const freshCallClient = new CallClient();
  
      const deviceManager = await freshCallClient.getDeviceManager();
      await navigator.mediaDevices.getUserMedia({ audio: true });
  
      const permission = await navigator.permissions.query({ name: PERMISSION_TYPES.MICROPHONE });
      if (permission.state !== PERMISSION_STATES.GRANTED) {
        await deviceManager.askDevicePermission(true, false);
      }
  
      const mics = await deviceManager.getMicrophones();
      if (!mics.length) {
        console.warn(INCOMING_CALLS_ERRORS.NO_MICROPHONES);
      }
      
      console.log(SUCCESS_MESSAGES.CALL_STARTED);
      const freshCallAgent = await freshCallClient.createCallAgent(tokenCredential, {
        displayName: CALL_CONFIG.DISPLAY_NAME,
      });
  
      // Save agent and bind incoming call handler
      setCallAgent(freshCallAgent);
      callAgentRef.current = freshCallAgent;

      freshCallAgent.on("incomingCall", async (incomingCallEvent) => {
        const incomingCall = incomingCallEvent.incomingCall;
        try {
          const acceptedCall = await incomingCall.accept({ audioOptions: CALL_CONFIG.AUDIO_OPTIONS });
          console.log(SUCCESS_MESSAGES.OFFER_ACCEPTED, acceptedCall);

          setCall(acceptedCall);
          callRef.current = acceptedCall;

          // Optionally listen to call state updates
          acceptedCall.on("stateChanged", () => {
            console.log(CONSOLE_MESSAGES.CALL_STATE_CHANGED, acceptedCall.state);
            if (acceptedCall.state === CALL_STATES.DISCONNECTED) {
              setCallState(CALL_STATES.DISCONNECTED);
              callRef.current = null;
              setDuration(0);
            }
          });
        } catch (err) {
          console.error(INCOMING_CALLS_ERRORS.FAILED_ACCEPT_CALL, err);
        }
      });

      console.log(SUCCESS_MESSAGES.TRANSFER_INITIATED);
      const response = await initiateCall({
        customerConnectionId: acceptedOffer.channelReference,
        agentAcsId,
      });
  
      if (response.status !== HTTP_STATUS.CREATED || !response.data?.call) {
        console.error(INCOMING_CALLS_ERRORS.BACKEND_CALL_FAILED, response.data);
        return;
      }
  
      console.log(SUCCESS_MESSAGES.BACKEND_CONNECTED, response.data.call);
    } catch (error) {
      console.error(INCOMING_CALLS_ERRORS.INITIATE_CALL_ERROR, error?.message || error);
      console.error(error);
    }
  };
    
  const handleHangUp = async () => {
    try {
      if (callRef.current) {
        await callRef.current.hangUp(CALL_CONFIG.HANGUP_OPTIONS); // Use forEveryone: true if agent should hang up for all
        console.log(SUCCESS_MESSAGES.CALL_HUNG_UP);
        callRef.current = null;
        setCall(null); // Reset call state
        setCallState(CALL_STATES.DISCONNECTED);
      }
  
      if (callAgentRef.current) {
        await callAgentRef.current.dispose();
        console.log(SUCCESS_MESSAGES.CALL_AGENT_DISPOSED);
        callAgentRef.current = null;
        setCallAgent(null); // Reset callAgent state
      }
  
      setDuration(0);
    } catch (err) {
      console.error(INCOMING_CALLS_ERRORS.HANGUP_FAILED, err);
    }
  };
  

  const handleCompleteAssignment = async () => {
    if (!acceptedOffer?.jobId) return;
    try {
      await completeIvrAssignment({assignmentId, workerId, jobId: acceptedOffer.jobId});
     /*  {
        assignmentId: selectedAssignment.id,
        workerId: selectedAssignment.workerId,
        jobId: selectedAssignment.jobid,
      } */
      await closeIvrAssignment({assignmentId, workerId, jobId: acceptedOffer.jobId});
      setAcceptedOffer(null);
      console.log(SUCCESS_MESSAGES.ASSIGNMENT_COMPLETED);
    } catch (err) {
      console.error(INCOMING_CALLS_ERRORS.COMPLETE_ASSIGNMENT_FAILED, err);
    }
  };

  const toggleMute = async () => {
    try {
      const call = callRef.current;
      if (!call) return;

      if (isMuted) {
        await call.unmute();
        setIsMuted(false);
      } else {
        await call.mute();
        setIsMuted(true);
      }
    } catch (err) {
      console.error(INCOMING_CALLS_ERRORS.MUTE_TOGGLE_ERROR, err);
    }
  };

  return (
    <Box sx={{
      display: DISPLAY.FLEX,
      flexWrap: "wrap",
      gap: UI_CONFIG.GAP,
      mb: UI_CONFIG.MARGIN_BOTTOM,
    }}>
      <Box sx={{ display: DISPLAY.FLEX , gap: UI_CONFIG.GAP }}>
      <Typography mb={UI_CONFIG.MARGIN_BOTTOM}>{INCOMING_CALLS_STRINGS.CALL_PANEL}</Typography>
      </Box>

      <Box sx={{ display: DISPLAY.FLEX, gap: UI_CONFIG.GAP }}>
        <Button
          variant={VARIANTS.CONTAINED}
          onClick={handleAcceptOffer}
          disabled={!jobOffer}
          sx={{ width: UI_CONFIG.BUTTON_WIDTH, 
            animation: jobOffer
              ? blink
                ? `blinker ${ANIMATION_CONFIG.BLINK_DURATION} ${ANIMATION_CONFIG.BLINK_TIMING} ${ANIMATION_CONFIG.BLINK_ITERATION}`
                : "none"
              : "none",
            "@keyframes blinker": {
              "50%": { opacity: 0 },
            },
          }}
        >
          {INCOMING_CALLS_STRINGS.ACCEPT_CALL}
        </Button>

        <Button
          variant={VARIANTS.CONTAINED}
          color="success"
          onClick={handleInitiateCall}
          disabled={!acceptedOffer}
          sx={{ width: UI_CONFIG.BUTTON_WIDTH }}
        >
          {INCOMING_CALLS_STRINGS.START_CALL}
        </Button>
      </Box>

      <Box sx={{ display: DISPLAY.FLEX, gap: UI_CONFIG.GAP }}>
        <Button
          variant={VARIANTS.CONTAINED}
          color="error"
          onClick={handleHangUp}
          disabled={!acceptedOffer}
          sx={{ width: UI_CONFIG.BUTTON_WIDTH }}
        >
          {INCOMING_CALLS_STRINGS.DISCONNECT_HANGUP}
        </Button>

        <Button
          variant={VARIANTS.CONTAINED}
          color="success"
          onClick={handleCompleteAssignment}
          disabled={!acceptedOffer}
          sx={{ width: UI_CONFIG.BUTTON_WIDTH }}
        >
          {INCOMING_CALLS_STRINGS.COMPLETE_ASSIGNMENT}
        </Button>
      </Box>


      <Grid container spacing={2} mt={UI_CONFIG.MARGIN_TOP}>
        {/* Row 1 - Mute Button */}
        <Grid item xs={6}>
          <Button
            variant={VARIANTS.CONTAINED}
            onClick={toggleMute}
            disabled={!callRef.current || callState === CALL_STATES.DISCONNECTED}
            //fullWidth
            sx={{ width: UI_CONFIG.BUTTON_WIDTH }}
          >
            {isMuted ? INCOMING_CALLS_STRINGS.UNMUTE : INCOMING_CALLS_STRINGS.MUTE}
          </Button>
        </Grid>
        <Grid item xs={6}>
        <Button
            variant={VARIANTS.CONTAINED}
            //onClick={toggleMute}
            disabled={!callRef.current || callState === CALL_STATES.DISCONNECTED}
            sx={{ width: UI_CONFIG.BUTTON_WIDTH }}
          >
            {INCOMING_CALLS_STRINGS.TRANSFER}
          </Button>
        </Grid>

        {/* Row 2 - Call Status and Duration */}
        <Grid item xs={6}>
          <Typography>{INCOMING_CALLS_STRINGS.CALL_STATUS} {callState}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>
            {INCOMING_CALLS_STRINGS.DURATION} {Math.floor(duration / 60)}:
            {String(duration % 60).padStart(2, "0")}
          </Typography>
        </Grid>
      </Grid>

      {jobOffer && (
        <Box mb={UI_CONFIG.MARGIN_BOTTOM}>
          <Typography fontWeight="bold">{INCOMING_CALLS_STRINGS.INCOMING_JOB_OFFER}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.JOB_ID} {jobOffer.jobId}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.OFFER_ID} {jobOffer.offerId}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.CONNECTION_ID} {jobOffer.channelReference}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.CUSTOMER_PHONE_NUMBER} {jobOffer.customerAcsId}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.CUSTOMER_GUID} {jobOffer.customerguid}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.WORKER_ID} {workerId}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.AGENT_ACS_ID} {agentAcsId}</Typography>
        </Box>
      )}

      {acceptedOffer && (
        <Box mb={UI_CONFIG.MARGIN_BOTTOM}>
          <Typography fontWeight="bold">{INCOMING_CALLS_STRINGS.ACCEPTED_OFFER}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.JOB_ID} {acceptedOffer.jobId}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.OFFER_ID} {acceptedOffer.offerId}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.CONNECTION_ID} {acceptedOffer.channelReference}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.CUSTOMER_PHONE_NUMBER} {acceptedOffer.customerAcsId}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.CUSTOMER_GUID} {acceptedOffer.customerguid}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.WORKER_ID} {workerId}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.AGENT_ACS_ID} {agentAcsId}</Typography>
          <Typography>{INCOMING_CALLS_STRINGS.ASSIGNMENT_ID} {assignmentId}</Typography>
        </Box>
      )}

    </Box>
  );
};

export default IncomingCall;




